import React, { useEffect, useState } from 'react';
import { Button, Paper, Box, Slide, Alert, AlertTitle } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import { createTranslationField } from '@/app/utils/i18nUtils.ts';
import type { I18nCommonNs } from '@/i18n/dictionaries/interfaces.ts';

/**
 * VersionChecker component
 * Periodically checks for a new application version and displays a notification
 * when a new version is available
 */
const VersionChecker = () => {
  const [outdated, setOutdated] = useState(false);
  const [currentBuildHash, setCurrentBuildHash] = useState('');
  const { t } = useTranslation(I18nNamespace.Common);
  const i18n = createTranslationField<I18nCommonNs>();
  // Check interval based on environment:
  // - Production: check every hour (3600000 ms)
  // - Local: check every 10 minutes (600000 ms)
  // - Development: check every 5 minutes (300000 ms)
  const checkInterval =
    process.env.NODE_ENV === 'production' ? 3600000 : process.env.NODE_ENV === 'local' ? 600000 : 300000;

  // First load - get the current hash
  useEffect(() => {
    const getCurrentHash = async () => {
      try {
        // Use a relative path (like how favicon works)
        // By removing the leading slash, it becomes relative to the current page
        const response = await fetch('/version.json');
        if (response.ok) {
          const data = await response.json();
          setCurrentBuildHash(data.buildHash);
          // Initial build hash loaded
        }
      } catch (error) {
        console.error('Failed to get initial build hash:', error);
      }
    };

    getCurrentHash();
  }, []);

  // Version checking effect
  useEffect(() => {
    // Only start checking after we have the current hash
    if (!currentBuildHash) return;

    // Function to check for new version
    const checkVersion = async () => {
      try {
        // Request version info with cache busting query param
        // Use absolute path to ensure it always fetches from root
        const response = await fetch(`/version.json?t=${Date.now()}`);
        if (response.ok) {
          const data = await response.json();

          // If server build hash is different than current build hash
          if (data.buildHash && data.buildHash !== currentBuildHash) {
            setOutdated(true);
          }
        }
      } catch (error) {
        console.error('Version check failed:', error);
      }
    };

    // Set interval to periodically check
    const interval = setInterval(checkVersion, checkInterval);

    return () => clearInterval(interval);
  }, [currentBuildHash, checkInterval]);

  // Show update notification if outdated
  if (outdated) {
    return (
      <Slide direction="up" in={outdated}>
        <Paper
          elevation={6}
          sx={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            zIndex: 9999,
            maxWidth: '320px',

            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              },
              '50%': {
                transform: 'scale(1.05)',
                boxShadow: '0 4px 25px rgba(0, 0, 0, 0.4)'
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.3)'
              }
            }
          }}
        >
          <Alert severity="info" sx={{ p: 2 }}>
            <AlertTitle>{t(i18n.versionChecker.newVersionAvailable)}</AlertTitle>
            {t(i18n.versionChecker.updateToGetLatestFeatures)}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button color="info" size="small" variant="contained" onClick={() => window.location.reload()}>
                {t(i18n.versionChecker.updateNow)}
              </Button>
            </Box>
          </Alert>
        </Paper>
      </Slide>
    );
  }

  return null;
};

export default VersionChecker;
